import PropTypes from 'prop-types'
import {
  FormGroup,
  FormControlLabel,
  Switch as MuiSwitch,
  Stack,
  Typography,
  IconButton,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useController, useFormContext } from 'react-hook-form'

import Tooltip from '../tooltip/tooltip.component'

import { SWITCH_TYPES } from './switch.constants'
import { styles } from './switch.styles'

const Switch = ({
  label,
  type,
  defaultChecked,
  rules,
  name,
  warning,
  style,
  disabled,
  reversedLabel,
  onChangeCallback,
  ...props
}) => {
  const { control } = useFormContext()
  const {
    field: { value, onChange, ref },
  } = useController({
    control,
    defaultValue: defaultChecked,
    name,
    rules,
    ...props,
  })
  const isChecked = Boolean(value)

  const inputProps = {
    'aria-label': label,
    ...props.inputProps,
  }

  const LabelInfo = () => (
    <Stack direction="row" spacing={1} sx={styles.labelWrapper}>
      <Typography
        sx={[isChecked ? styles.labelSelected : styles.labelOnly, disabled && styles.disabledLabel]}
      >
        {label}
      </Typography>
      {warning && (
        <Tooltip placement="bottom" title={warning}>
          <IconButton sx={styles.tooltip} disableRipple tabIndex={-1}>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  )

  const handleChange = (e, checked) => {
    onChange(e, checked)
    if (onChangeCallback) {
      onChangeCallback(e, checked)
    }
  }

  const mapComponent = {
    [SWITCH_TYPES.SIMPLE]: (
      <MuiSwitch
        sx={[styles.simple, style]}
        color="primary"
        inputRef={ref}
        checked={isChecked}
        name={name}
        inputProps={inputProps}
        onChange={handleChange}
        defaultChecked={defaultChecked}
        /*NOTE: This disable was not present before, but is implemented
        to disable toggle behaviour.
        It will be removed lateron if not required
         */
        disabled={disabled}
        {...props}
      />
    ),
    [SWITCH_TYPES.LABEL]: (
      <FormControlLabel
        sx={[styles.label, reversedLabel && styles.labelReverse]}
        onChange={handleChange}
        inputRef={ref}
        name={name}
        label={<LabelInfo />}
        control={
          <MuiSwitch
            onChange={handleChange}
            inputRef={ref}
            checked={isChecked}
            name={name}
            color="primary"
            disabled={disabled}
            inputProps={inputProps}
            {...props}
          />
        }
      />
    ),
    [SWITCH_TYPES.BOX]: (
      <FormControlLabel
        sx={[styles.box, isChecked && styles.boxSelected, style]}
        onChange={handleChange}
        inputRef={ref}
        label={<LabelInfo />}
        labelPlacement="start"
        control={
          <MuiSwitch
            color="primary"
            inputProps={inputProps}
            checked={isChecked}
            onChange={handleChange}
            inputRef={ref}
            name={name}
            {...props}
          />
        }
      />
    ),
  }

  return <FormGroup>{mapComponent[type]}</FormGroup>
}

Switch.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(SWITCH_TYPES)).isRequired,
  defaultChecked: PropTypes.bool,
  inputProps: PropTypes.shape(),
  warning: PropTypes.string,
  style: PropTypes.shape().isRequired,
  rules: PropTypes.object,
  disabled: PropTypes.bool,
  reversedLabel: PropTypes.bool,
  onChangeCallback: PropTypes.func,
}

Switch.defaultProps = {
  inputProps: {},
  defaultChecked: false,
  warning: '',
  style: {},
  disabled: false,
  reversedLabel: false,
  onChangeCallback: () => {},
}

export default Switch
